import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { File as FileIcon, FileImage, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { formatearBytes, formatearFechaHora } from '@/lib/tareas';
import { abrirAdjunto, useAdjuntoMutations, useAdjuntos } from '@/hooks/useTareasDetalle';
import { usePermisosTareas } from '@/hooks/useTareas';
import { ADJUNTO_MAX_BYTES, type AdjuntoTarea, type TareaDetalle } from '@/types/tareas';

const TIPOS_ACEPTADOS = 'image/jpeg,image/png,image/webp,image/gif,application/pdf,.doc,.docx';

function icono(mime: string) {
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf' || mime.includes('word')) return FileText;
  return FileIcon;
}

export function AdjuntosPanel({ tarea }: { tarea: TareaDetalle }) {
  const usuario = useAuthStore(s => s.usuario);
  const { esAdmin } = usePermisosTareas();
  const { data: adjuntos = [], isLoading } = useAdjuntos(tarea.id);
  const mut = useAdjuntoMutations(tarea.id, tarea.tableroId);
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const subir = (archivos: FileList | File[]) => {
    Array.from(archivos).forEach(archivo => {
      if (archivo.size > ADJUNTO_MAX_BYTES) {
        toast.error(`"${archivo.name}" supera el límite de 10 MB`);
        return;
      }
      mut.subir.mutate(archivo);
    });
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={e => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={e => { e.preventDefault(); setArrastrando(false); if (e.dataTransfer.files.length) subir(e.dataTransfer.files); }}
        className={cn('rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors', arrastrando ? 'border-primary-400 bg-primary-50' : 'border-gray-200')}
      >
        <Upload className="mx-auto text-gray-300" size={26} />
        <p className="mt-2 text-sm text-text-primary">Arrastra archivos aquí o{' '}
          <button onClick={() => entrada.current?.click()} className="text-primary-600 font-medium hover:underline">elígelos</button>
        </p>
        <p className="text-[11px] text-text-secondary">Imágenes, PDF o Word · máximo 10 MB por archivo · cuentan en tu almacenamiento</p>
        <input
          ref={entrada}
          type="file"
          multiple
          accept={TIPOS_ACEPTADOS}
          className="hidden"
          onChange={e => { if (e.target.files?.length) subir(e.target.files); e.target.value = ''; }}
        />
        {mut.subir.isPending && <p className="mt-2 text-sm text-text-secondary inline-flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Subiendo…</p>}
      </div>

      {isLoading && <p className="text-sm text-text-secondary">Cargando…</p>}
      {!isLoading && adjuntos.length === 0 && <p className="text-sm text-text-secondary">Sin adjuntos.</p>}

      <ul className="space-y-1.5">
        {adjuntos.map((a: AdjuntoTarea) => {
          const Icono = icono(a.mimeType);
          const puedeEliminar = esAdmin || a.subidoPorId === usuario?.id;
          return (
            <li key={a.id} className="flex items-center gap-3 rounded-lg border border-border bg-white px-3 py-2">
              <Icono size={18} className="text-primary-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <button onClick={() => abrirAdjunto(a.id)} className="block max-w-full text-left text-sm font-medium text-text-primary hover:text-primary-700 truncate">
                  {a.nombreArchivo}
                </button>
                <p className="text-[11px] text-text-secondary">{formatearBytes(a.bytes)} · {a.subidoPorNombre} · {formatearFechaHora(a.createdAt)}</p>
              </div>
              {puedeEliminar && (confirmando === a.id ? (
                <span className="flex items-center gap-1">
                  <button onClick={() => { mut.eliminar.mutate(a.id); setConfirmando(null); }} className="px-2 py-1 rounded-md bg-red-500 text-white text-xs">Eliminar</button>
                  <button onClick={() => setConfirmando(null)} className="px-2 py-1 rounded-md border border-gray-200 text-xs">Cancelar</button>
                </span>
              ) : (
                <button onClick={() => setConfirmando(a.id)} className="p-1.5 text-gray-400 hover:text-red-500" aria-label="Eliminar adjunto">
                  <Trash2 size={14} />
                </button>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
